import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { RefreshCcw, Search } from "lucide-react";
import { useToaster } from "../../contexts/ToasterContext";
import {
  getIntegrationConfig,
  getIntegrationEndpoint,
  isValidIntegrationType,
} from "../../config/integrationConfig";
import IntegrationCardGrid from "../../components/Integration/IntegrationCardGrid";
import IntegrationStats from "../../components/Integration/IntegrationStats";
import { CardShimmerGrid } from "../../components/Shimmer";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import Pagination from "../../components/ui/pagination";
import { getGitHubRepo, getS3Bucket } from "../../utils/mockData";

// Helper function to get value from nested object path
const getValueFromPath = (obj, path) => {
  if (!obj || !path || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

// Seeded random number generator for deterministic mock data
const seededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};

// Helper function to convert string to integer seed
function hashStringToInt(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Mock data generators
const getMockIntegrations = (type, page = 1, perPage = 50) => {
  const generateMockItem = (i, integrationType) => {
    // Create a seeded RNG based on item ID to ensure deterministic data
    const itemId = `${integrationType}-${i + 1}`;
    const seed = hashStringToInt(itemId);
    const random = seededRandom(seed);
    const baseTimestamp = new Date('2024-01-01').getTime();
    
    const status = random() > 0.7 ? 'scanning' : 'active';
    const totalFindings = Math.floor(random() * 100);
    const verifiedFindings = Math.floor(random() * 50);
    const daysAgoCreated = Math.floor(random() * 365);
    const daysAgoLastScan = Math.floor(random() * 30);
    
    const baseItem = {
      id: itemId,
      integration_id: itemId,
      type: integrationType,
      status: status,
      totalFindings: totalFindings,
      verifiedFindings: verifiedFindings,
      created_at: new Date(baseTimestamp + daysAgoCreated * 24 * 60 * 60 * 1000).toISOString(),
      lastScan: new Date(baseTimestamp + daysAgoLastScan * 24 * 60 * 60 * 1000).toISOString()
    };

    // Type-specific fields
    switch (integrationType) {
      case 's3':
        const s3Bucket = getS3Bucket(i);
        return {
          ...baseItem,
          name: s3Bucket.name,
          bucket_name: s3Bucket.name,
          region: s3Bucket.region
        };
      case 'github':
        const githubRepo = getGitHubRepo(i);
        return {
          ...baseItem,
          name: githubRepo.name,
          owner: githubRepo.owner,
          full_name: githubRepo.fullName,
          states: {
            isScanningCount: Math.floor(random() * 3),
            totalCount: Math.floor(random() * 10)
          }
        };
      // case 'azure':
      //   return {
      //     ...baseItem,
      //     name: `resource-${i + 1}`,
      //     resourceGroup: `rg-${(i % 5) + 1}`,
      //     subscription: `sub-${(i % 3) + 1}`
      //   };
      // case 'aws':
      //   return {
      //     ...baseItem,
      //     name: `service-${i + 1}`,
      //     region: ['us-east-1', 'us-west-2', 'eu-west-1'][i % 3],
      //     serviceType: ['ec2', 's3', 'lambda', 'rds'][i % 4]
      //   };
      // case 'gcs':
      //   return {
      //     ...baseItem,
      //     name: `bucket-${i + 1}`,
      //     project: `project-${(i % 5) + 1}`,
      //     location: ['us', 'eu', 'asia'][i % 3]
      //   };
      // case 'teams':
      //   return {
      //     ...baseItem,
      //     name: `channel-${i + 1}`,
      //     channel_name: `channel-${i + 1}`,
      //     team_name: `Team ${(i % 5) + 1}`,
      //     team_id: `team-${(i % 5) + 1}`
      //   };
      default:
        return {
          ...baseItem,
          name: `${integrationType}-item-${i + 1}`
        };
    }
  };

  // Limit items for GitHub and S3 to 20-25 items
  const totalItems = (type === 'github' || type === 's3') ? 22 : 150;
  const allItems = Array.from({ length: totalItems }, (_, i) => generateMockItem(i, type));
  const start = (page - 1) * perPage;
  const end = start + perPage;
  const paginatedItems = allItems.slice(start, end);

  return {
    data: paginatedItems,
    pagination: {
      page,
      per_page: perPage,
      last_page: Math.ceil(totalItems / perPage),
      has_next: page < Math.ceil(totalItems / perPage),
      has_previous: page > 1,
      total_count: totalItems
    }
  };
};

const IntegrationTypePage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const [loading, setLoading] = useState(true);
  const [integrationSearchQuery, setIntegrationSearchQuery] = useState("");
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalIntegrations, setTotalIntegrations] = useState(0);

  const config = getIntegrationConfig(type);

  // Redirect if invalid integration type
  useEffect(() => {
    if (!isValidIntegrationType(type)) {
      navigate("/integration", { replace: true });
      showToast(`Invalid integration type: ${type}`, "error");
      return;
    }
  }, [type, navigate, showToast]);

  const fetchIntegrations = useCallback(async (isPolling = false) => {
    if (!config) return;

    const query = integrationSearchQuery.trim();
    const isSearching = query !== "";

    try {
      if (!isPolling) setLoading(true);
      setError(null);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, isPolling ? 200 : 500));
      
      // For active-directory, use config's mockIntegrationsData
      let mockResponse;
      if (type === "active-directory" && config?.mockIntegrationsData) {
        const mockData = config.mockIntegrationsData;
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        const paginatedItems = mockData.slice(start, end);
        mockResponse = {
          data: paginatedItems,
          pagination: {
            page: currentPage,
            per_page: itemsPerPage,
            last_page: Math.ceil(mockData.length / itemsPerPage),
            has_next: currentPage < Math.ceil(mockData.length / itemsPerPage),
            has_previous: currentPage > 1,
            total_count: mockData.length
          }
        };
      } else {
        // Use mock data generator for other types
        mockResponse = getMockIntegrations(type, currentPage, itemsPerPage);
      }
      
      let data = [];
      const res = mockResponse;

      // Apply type-specific normalization (same logic as before but on mock data)
      if (type === "s3") {
        data = res.data.map((bucket) => ({
          id: bucket.id || bucket.Name || bucket.name,
          integration_id: bucket.id || bucket.Name || bucket.name,
          name: bucket.name || bucket.Name || `Bucket ${bucket.id || ""}`,
          bucket_name: bucket.name || bucket.Name,
          region: bucket.region || "N/A",
          type: "s3",
          status: bucket.isScanning ? "scanning" : "active",
          totalFindings: bucket.totalFindings !== undefined ? bucket.totalFindings : ((bucket.activeFindings || 0) + (bucket.inactiveFindings || 0)),
          verifiedFindings: bucket.verifiedFindings !== undefined ? bucket.verifiedFindings : (bucket.activeFindings || 0),
          lastScan: bucket.creationDate || bucket.created_at || bucket.lastScan || null,
          created_at: bucket.creationDate || bucket.created_at || null,
        }));
      } else if (type === "github") {
        data = res.data.map((repo) => ({
          id: repo.repository_id || repo.id,
          integration_id: repo.repository_id || repo.id,
          name: repo.name || repo.full_name?.split("/")?.[1] || "unknown",
          owner: repo.owner || repo.full_name?.split("/")?.[0] || "",
          full_name: repo.full_name || (repo.owner && repo.name ? `${repo.owner}/${repo.name}` : ""),
          type: "github",
          status: repo.states?.isScanningCount > 0 ? "scanning" : "active",
          totalFindings: repo.totalFindings || 0,
          verifiedFindings: repo.verifiedFindings || 0,
          lastScan: repo.updated_at || repo.created_at || null,
          created_at: repo.created_at || null,
          states: repo.states || { isScanningCount: 0, totalCount: 0 },
        }));
      } else if (type === "azure") {
        data = res.data.map((resource) => ({
          id: resource.id || resource.resource_id || resource.name,
          integration_id: resource.id || resource.resource_id || resource.name,
          name: resource.name || resource.resourceName || `Resource ${resource.id || ""}`,
          resourceGroup: resource.resourceGroup || resource.resource_group || "N/A",
          subscription: resource.subscription || resource.subscription_id || "N/A",
          type: "azure",
          status: resource.isScanning || resource.is_scanning ? "scanning" : "active",
          totalFindings: resource.totalFindings || (resource.activeFindings || 0) + (resource.inactiveFindings || 0),
          verifiedFindings: resource.activeFindings || resource.verifiedFindings || 0,
          lastScan: resource.lastScan || resource.updated_at || resource.created_at || null,
          created_at: resource.created_at || null,
        }));
      } else if (type === "aws") {
        data = res.data.map((service) => ({
          id: service.id || service.service_id || service.name,
          integration_id: service.id || service.service_id || service.name,
          name: service.name || service.serviceName || `Service ${service.id || ""}`,
          region: service.region || service.aws_region || "N/A",
          serviceType: service.serviceType || service.service_type || "N/A",
          type: "aws",
          status: service.isScanning || service.is_scanning ? "scanning" : "active",
          totalFindings: service.totalFindings || (service.activeFindings || 0) + (service.inactiveFindings || 0),
          verifiedFindings: service.activeFindings || service.verifiedFindings || 0,
          lastScan: service.lastScan || service.updated_at || service.created_at || null,
          created_at: service.created_at || null,
        }));
      } else if (type === "gcs") {
        data = res.data.map((bucket) => ({
          id: bucket.id || bucket.bucket_id || bucket.name,
          integration_id: bucket.id || bucket.bucket_id || bucket.name,
          name: bucket.name || bucket.bucket_name || `Bucket ${bucket.id || ""}`,
          project: bucket.project || bucket.project_id || "N/A",
          location: bucket.location || bucket.region || "N/A",
          type: "gcs",
          status: bucket.isScanning || bucket.is_scanning ? "scanning" : "active",
          totalFindings: bucket.totalFindings || (bucket.activeFindings || 0) + (bucket.inactiveFindings || 0),
          verifiedFindings: bucket.activeFindings || bucket.verifiedFindings || 0,
          lastScan: bucket.lastScan || bucket.updated_at || bucket.created_at || null,
          created_at: bucket.created_at || null,
        }));
      } else if (["bitbucket", "gitlab"].includes(type)) {
        data = res.data.map((repo) => ({
          id: repo.repository_id || repo.id,
          integration_id: repo.repository_id || repo.id,
          name: repo.name || repo.full_name?.split("/")?.[1] || "unknown",
          owner: repo.owner || repo.workspace || repo.namespace || repo.full_name?.split("/")?.[0] || "",
          full_name: repo.full_name || (repo.owner && repo.name ? `${repo.owner}/${repo.name}` : ""),
          type: type,
          status: repo.states?.isScanningCount > 0 ? "scanning" : "active",
          totalFindings: repo.totalFindings || 0,
          verifiedFindings: repo.verifiedFindings || 0,
          lastScan: repo.updated_at || repo.created_at || null,
          created_at: repo.created_at || null,
          states: repo.states || { isScanningCount: 0, totalCount: 0 },
        }));
      } else if (type === "huggingface") {
        data = res.data.map((model) => ({
          id: model.id || model.model_id || model.name,
          integration_id: model.id || model.model_id || model.name,
          name: model.name || model.model_name || `Model ${model.id || ""}`,
          author: model.author || model.owner || "N/A",
          modelType: model.modelType || model.model_type || model.pipeline_tag || "N/A",
          type: "huggingface",
          status: model.isScanning || model.is_scanning ? "scanning" : "active",
          totalFindings: model.totalFindings || (model.activeFindings || 0) + (model.inactiveFindings || 0),
          verifiedFindings: model.activeFindings || model.verifiedFindings || 0,
          lastScan: model.lastScan || model.updated_at || model.created_at || null,
          created_at: model.created_at || null,
        }));
      } else if (type === "active-directory") {
        // Active Directory data is already in the correct format from config
        data = res.data.map((domain) => ({
          id: domain.id || domain.integration_id,
          integration_id: domain.integration_id || domain.id,
          name: domain.name || `Domain ${domain.id || ""}`,
          domain_controller: domain.domain_controller,
          stats: domain.stats,
          type: "active-directory",
          status: domain.status || "active",
          risk_level: domain.risk_level,
          predictability: domain.predictability,
          totalFindings: domain.totalFindings || 0,
          verifiedFindings: domain.verifiedFindings || 0,
          lastScan: domain.created_at || null,
          created_at: domain.created_at || null,
        }));
      } else if (type === "teams") {
        data = res.data.map((channel) => ({
          id: channel.channel_id || channel.id,
          integration_id: channel.channel_id || channel.id,
          name: channel.channel_name || `Channel ${channel.channel_id || ""}`,
          team_name: channel.team_name || "Unknown Team",
          team_id: channel.team_id,
          type: "teams",
          status: (channel.total_alerts > 0) ? "active" : "secure",
          totalFindings: channel.total_alerts || 0,
          verifiedFindings: channel.verified_alerts || 0,
          unverifiedFindings: channel.unverified_alerts || 0,
          detector_types: channel.detector_types || [],
          has_active_findings: channel.has_active_findings || false,
          lastScan: channel.last_alert_at || channel.created_at || null,
          created_at: channel.created_at || null,
        }));
      } else {
        data = res.data.map((item) => ({
          id: item.id || item.name || Math.random().toString(36).substr(2, 9),
          integration_id: item.id || item.name,
          name: item.name || item.title || `Item ${item.id || ""}`,
          type: type,
          status: item.isScanning || item.is_scanning ? "scanning" : item.status || "active",
          totalFindings: item.totalFindings || (item.activeFindings || 0) + (item.inactiveFindings || 0),
          verifiedFindings: item.activeFindings || item.verifiedFindings || 0,
          lastScan: item.lastScan || item.updated_at || item.created_at || null,
          created_at: item.created_at || null,
        }));
      }

      // Sort integrations: Active (verifiedFindings > 0) first, then inactive (verifiedFindings = 0)
      data.sort((a, b) => {
        const aIsActive = (a.verifiedFindings || 0) > 0;
        const bIsActive = (b.verifiedFindings || 0) > 0;
        
        // Active comes first
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
        // If both have same status, maintain original order
        return 0;
      });

      setIntegrations(data);
      setTotalIntegrations(res.pagination?.total_count || data.length);
    } catch (err) {
      console.error(`Error fetching ${type} integrations:`, err);
      const errorMessage = `Failed to fetch ${type} integrations`;
      setError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }, [config, type, showToast, currentPage, itemsPerPage, integrationSearchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (config) {
        fetchIntegrations();
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [config, type, fetchIntegrations]);

  // Add polling for list updates every 30 seconds
  useEffect(() => {
    if (!config || integrationSearchQuery.trim() !== "") return;

    // Set up polling interval
    const pollInterval = setInterval(() => {
      fetchIntegrations(true);
    }, 30000); // 30 seconds

    // Cleanup interval on unmount or config change
    return () => {
      clearInterval(pollInterval);
    };
  }, [config, fetchIntegrations, integrationSearchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (number) => {
    setItemsPerPage(number);
    setCurrentPage(1); // Reset to first page
  };

  const isIntegrationProcessing = (integration) => {
    return (
      integration.status === "scanning" || integration.is_processing === true
    );
  };

  if (!config) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Integration type not found
          </h2>
          <p className="text-gray-600 mb-4">
            The integration type "{type}" is not supported.
          </p>
          <Link
            to="/integration"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#441a9a] hover:bg-[#3a1080]"
          >
            Back to Integrations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/identity-management">Identity Management</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{config.displayName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {type === "active-directory" ? (
            <>
              <h1 className="text-black text-2xl md:text-3xl font-bold">
                Service Accounts Identity Management
              </h1>
              <p className="text-gray-600 mt-2">
                Active Directory security monitoring and identity management • {totalIntegrations} configured
              </p>
            </>
          ) : (
            <>
              <h1 className="text-black text-2xl md:text-3xl font-bold">
                {config.displayName} Integrations
              </h1>
              <p className="text-gray-600 mt-2">
                {config.description} • {totalIntegrations} configured
              </p>
            </>
          )}
        </div>

        {/* Analytics Stats - Custom for Active Directory */}
        {type === "active-directory" ? (
          <>
            <Separator className="my-6" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
              {config.statsCards?.map((stat, index) => {
                const statsData = config.mockStatsData || {};
                let value = getValueFromPath(statsData, stat.dataPath);
                if (stat.type === "progress_bar") {
                  const protectedCount = value?.protected || 0;
                  const unprotectedCount = value?.unprotected || 0;
                  const total = protectedCount + unprotectedCount;
                  return (
                    <Card key={index} className="flex-1 min-w-[200px]">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                          {stat.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{protectedCount} Protected Accounts</div>
                        <div className="text-sm text-gray-600 mt-1">{unprotectedCount} Unprotected</div>
                        <div className="mt-2 h-2 w-full bg-gray-200 rounded-full">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{
                              width: `${total > 0 ? (protectedCount / total) * 100 : 0}%`,
                            }}
                          ></div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }
                return (
                  <Card key={index} className="flex-1 min-w-[200px]">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {stat.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{value || 0}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Separator className="my-6" />
          </>
        ) : (
          <IntegrationStats integrationType={type} />
        )}

        {/* Search Bar */}
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row w-full lg:items-center justify-between gap-6">
              <div className="w-full lg:w-1/2">
                <div className="relative">
                  <input
                    type="text"
                    value={integrationSearchQuery}
                    onChange={(e) => setIntegrationSearchQuery(e.target.value)}
                    placeholder={`Search ${config.displayName.toLowerCase()} integrations...`}
                    className="w-full pl-10 pr-3 py-2 rounded border border-[#441a9a] bg-white text-[#000000] placeholder-[#8D82AA] focus:outline-none focus:border-[#441a9a] focus:ring-2 focus:ring-[#441a9a] focus:ring-offset-0 transition-colors"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D82AA]" />
                </div>
              </div>
              {config.onboarding?.enabled && (
                <div className="w-full lg:w-auto">
                  <Link
                    to={config.onboarding.route}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#441a9a] hover:bg-[#3a1080] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#441a9a] transition-colors"
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    {config.onboarding.buttonText}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content based on view mode */}
        {loading ? (
          <div className="mt-6">
            <CardShimmerGrid count={8} />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-[#000000] text-xl mb-2">{error}</p>
            <button
              onClick={fetchIntegrations}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#441a9a] hover:bg-[#3a1080] mt-4"
            >
              Retry
            </button>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#000000] text-xl mb-4">
              {integrationSearchQuery.trim()
                ? `No ${config.displayName.toLowerCase()} integrations found matching your search`
                : `No ${config.displayName.toLowerCase()} integrations configured`}
            </p>
            <p className="text-[#000000] text-xl">
              {integrationSearchQuery.trim()
                ? "Try a different search term or clear the search to see all integrations."
                : `Start by configuring your first ${config.displayName.toLowerCase()} integration from Identity Management.`}
            </p>
          </div>
        ) : (
          <div className="mt-6">
            <IntegrationCardGrid
              integrations={integrations}
              isIntegrationProcessing={isIntegrationProcessing}
              integrationType={type}
            />
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalIntegrations / itemsPerPage)}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalIntegrations}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationTypePage;
